// PassFirst — Vertical Package Config
//
// Every content package under `content/<slug>/` MUST export a default
// VerticalConfig object matching this schema.
//
// Architecture rule: NO per-vertical logic in app code. Any vertical-specific
// behavior is declared here via capability flags and read by the engine at runtime.

import { z } from "zod";

// ---------------------------------------------------------------------------
// Industry categories
// ---------------------------------------------------------------------------
// Presentation-level grouping for the credential picker (app /welcome) and
// the marketing site. NOT business logic: nothing branches on category — it
// only controls which section a credential renders under and the order of
// those sections. New categories are added here once and every surface picks
// them up. `order` is ascending (lower = earlier).
//
// This is the single source of truth for category labels + ordering. The DB
// stores only the category slug per vertical; labels/order are looked up here
// so renaming a section is a one-line change with no migration.
export const CATEGORIES = [
  { slug: "transportation", label: "Transportation", order: 10 },
  { slug: "maritime", label: "Maritime", order: 20 },
  { slug: "aviation", label: "Aviation", order: 30 },
  { slug: "finance-tax", label: "Finance & Tax", order: 40 },
  { slug: "real-estate", label: "Real Estate", order: 45 },
  { slug: "health-safety-environment", label: "Health, Safety & Environment", order: 50 },
  { slug: "telecommunications", label: "Telecommunications", order: 60 },
  { slug: "legal-professional", label: "Legal & Professional", order: 70 },
  { slug: "government-civics", label: "Government & Civics", order: 80 },
] as const;

export type CategorySlug = (typeof CATEGORIES)[number]["slug"];

const CATEGORY_SLUGS = CATEGORIES.map((c) => c.slug) as [
  CategorySlug,
  ...CategorySlug[],
];

// Look up a category's display metadata by slug. Returns a sensible fallback
// (title-cased slug, end-of-list order) for any slug not yet in CATEGORIES so
// a freshly-added vertical never renders an empty section header.
export function categoryMeta(slug: string): { label: string; order: number } {
  const found = CATEGORIES.find((c) => c.slug === slug);
  if (found) return { label: found.label, order: found.order };
  return {
    label: slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
    order: 999,
  };
}

/**
 * Resolve the canonical marketing landing-page path for a vertical — the URL
 * the SEO site actually builds from the vertical's first seoPattern. Use this
 * anywhere a credential tile/link points at "the vertical's landing page"
 * (index grid, category hubs) so the href always matches a route that
 * `[...slug].astro` emits.
 *
 * Mirrors that route's path generation. Prefers a state-NEUTRAL pattern (no
 * `{state}`) so a state-bound credential's tile lands on its hub page rather
 * than one arbitrary state — the hub lists the covered states and funnels into
 * the app's jurisdiction picker. Only when a vertical has no state-neutral
 * pattern do we fall back to a default jurisdiction (lowest code,
 * alphabetically) so the link still resolves to a page that exists. Returns
 * `/${slug}` only as a last-ditch fallback when a vertical has no seoPatterns
 * (which would itself be a bug).
 */
export function landingPath(
  slug: string,
  seoPatterns: ReadonlyArray<{ urlTemplate: string }>,
  jurisdictions: ReadonlyArray<{ code: string; name: string }>,
): string {
  if (seoPatterns.length === 0) return `/${slug}`;

  const fill = (template: string, vars: Record<string, string>): string =>
    template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);

  const vars: Record<string, string> = { vertical: slug };

  // Prefer the hub (state-neutral) pattern; fall back to the first pattern.
  const neutral = seoPatterns.find((p) => !p.urlTemplate.includes("{state}"));
  const pattern = neutral ?? seoPatterns[0]!;

  if (pattern.urlTemplate.includes("{state}")) {
    const defaultJurisdiction = jurisdictions
      .filter((j) => j.code.toUpperCase() !== "FEDERAL")
      .sort((a, b) => a.code.localeCompare(b.code))[0];
    // No non-FEDERAL jurisdiction to bind → no state page is generated for
    // this vertical; nothing better to point at than the bare slug.
    if (!defaultJurisdiction) return `/${slug}`;
    vars.state = defaultJurisdiction.code.toLowerCase();
    vars.stateName = defaultJurisdiction.name;
  }

  const path = fill(pattern.urlTemplate, vars);
  return path.startsWith("/") ? path : `/${path}`;
}

export const CapabilityFlagsSchema = z.object({
  stateSpecific: z.boolean(),
  voiceMode: z.boolean(),
  scenarioDrills: z.boolean(),
  endorsements: z.boolean(),
  recertification: z.boolean(),
  requiresDisclaimers: z.boolean(),
  // When true, a non-FEDERAL ingestion run classifies each draft as FEDERAL
  // (universal — re-tagged to the shared null jurisdiction) vs STATE-specific
  // (kept on the run's jurisdiction). Lets a state handbook's general content
  // flow into the shared federal pool so every state benefits and we don't
  // duplicate the curriculum per state. Optional; absent/false = skip scope.
  splitFederalState: z.boolean().optional(),
});

// Apple App Store + Google Play product IDs by tier. Native clients
// look these up to know which IAP SKU to purchase; the server uses the
// reverse mapping (productId → vertical+tier) when verifying receipts.
// Convention: com.ricekernel.passfirst.app.{vertical-slug-collapsed}.{tier} with dashes
// collapsed (Apple disallows them in some contexts; safer everywhere).
// Each tier optional — a vertical can ship subscription-only or
// oneoff-only on native if pricing differs from web.
export const IapProductIdsSchema = z.object({
  oneoff: z.string().optional(),
  monthly: z.string().optional(),
  annual: z.string().optional(),
});

// Optional promotional / introductory offer identifiers.
// These supplement iapProductIds for offer-aware purchase flows.
//
// Apple:
//   introductoryOfferIds — App Store Connect offer identifiers for
//   introductory pricing (first-time subscribers). Keyed by tier.
//   promotionalOfferIds  — Win-back / upgrade Promotional Offer
//   identifiers for existing/lapsed subscribers. Keyed by tier, then
//   by your label (arbitrary — use for UI copy selection).
//   The client passes the chosen identifier to react-native-iap's
//   requestPurchase({ withOffer: { offerIdentifier, keyIdentifier,
//   nonce, timestamp, signature } }) after getting a signed offer from
//   POST /trpc/iap.signAppleOffer (not yet implemented — see IAP-SETUP.md).
//
// Android:
//   googleBasePlanIds — base plan IDs for subscriptions. Google offer
//   tokens are resolved at runtime from productDetails returned by the
//   Play SDK; only the static base plan ID needs to live in config.
export const IapOfferIdsSchema = z.object({
  introductoryOfferIds: z
    .record(z.enum(["monthly", "annual"]), z.string())
    .optional(),
  promotionalOfferIds: z
    .record(z.enum(["monthly", "annual"]), z.record(z.string(), z.string()))
    .optional(),
  googleBasePlanIds: z
    .record(z.enum(["monthly", "annual"]), z.string())
    .optional(),
});

export const PricingConfigSchema = z.object({
  oneOffCents: z.number().int().positive(),
  subscriptionMonthlyCents: z.number().int().positive().nullable(),
  subscriptionAnnualCents: z.number().int().positive().nullable(),
  endorsementAddonCents: z.number().int().positive().nullable(),
  stripeProductIds: z.record(z.string(), z.string()).optional(),
  iapProductIds: IapProductIdsSchema.optional(),
  iapOfferIds: IapOfferIdsSchema.optional(),
});

export const SeoPatternSchema = z.object({
  urlTemplate: z.string(),
  titleTemplate: z.string(),
  h1Template: z.string(),
  metaDescriptionTemplate: z.string(),
});

export const VoiceExerciseSchema = z.object({
  slug: z.string(),
  label: z.string(),
  promptTemplate: z.string(),
  rubricPath: z.string(),
  maxTurns: z.number().int().positive(),
});

export const SourceRegistryEntrySchema = z.object({
  url: z.string().url(),
  label: z.string(),
  // Initial cadence hint at vertical-setup time. Largely informational
  // now that the canonical signal is `nextRefreshTimestamp` (mutable
  // per-source state, set after each successful ingestion). Kept so
  // existing content packages don't need to migrate immediately.
  pollFrequency: z.enum(["daily", "weekly", "biweekly"]),
  jurisdictionCode: z.string().optional(),
  // ISO-8601 timestamp for when this source is next due for review/
  // re-ingestion. Set by the pipeline (or operator) after a successful
  // ingestion to "now + N days" where N matches the regulatory
  // cadence appropriate for the source. The
  // mcp__passfirst-admin__list_due_refreshes tool surfaces sources
  // whose timestamp has passed so a Claude Code session can triage
  // them — re-ingest if the source has changed, push the timestamp
  // forward if not. Null/missing = "never reviewed yet" (fresh
  // vertical setup) which list_due_refreshes treats as due.
  nextRefreshTimestamp: z.string().datetime().nullable().optional(),
});

// Per-vertical overrides for the content pipeline's quality gates.
// Every field is optional — pipeline falls back to its defaults when
// absent. Tighter thresholds for clinical-scope verticals (CPR, EMS),
// looser for vendor-cert verticals where the source IS the spec.
export const QcThresholdsSchema = z.object({
  autoPromote: z
    .object({
      meanMin: z.number().min(1).max(5).optional(),
      perDimensionMin: z.number().min(1).max(5).optional(),
    })
    .optional(),
  rescue: z
    .object({
      threshold: z.number().min(1).max(5).optional(),
    })
    .optional(),
  dedup: z
    .object({
      threshold: z.number().min(0).max(1).optional(),
      // "lexical" = 4-char Jaccard (default; no API key needed).
      // "embedding" = Voyage cosine similarity (auto-selected when
      // VOYAGE_API_KEY is set unless this overrides).
      backend: z.enum(["lexical", "embedding"]).optional(),
    })
    .optional(),
  // Target pool size for blueprint coverage scoring. Larger verticals
  // (CDL, real estate) need more questions per leaf to feel "ready".
  coverage: z
    .object({
      // Explicit pool target. If unset, it's derived from examQuestionCount
      // (≈1.75×), else falls back to the engine default (200). Set this OR
      // examQuestionCount — prefer examQuestionCount, which is the real fact.
      targetPoolSize: z.number().int().positive().optional(),
      // The real exam's scored-question count (e.g. CA notary = 45, patent
      // bar = 100). The pool target derives from this so "ready" reflects
      // genuine sufficiency rather than an aspirational flat 200 — a single
      // 40-Q state-law exam does not need a 200-deep pool. See
      // resolveTargetPoolSize() + the 2026-06 expansion retro (P3).
      examQuestionCount: z.number().int().positive().optional(),
    })
    .optional(),
  // Readiness gate (consumed by tools/vertical-readiness.ts). Override
  // to make a clinical-scope vertical require more APPROVED before
  // shipping, or to relax limits for a low-volume cert.
  readiness: z
    .object({
      minTotalApproved: z.number().int().positive().optional(),
      minApprovedPerLeaf: z.number().int().positive().optional(),
      minJudgeMean: z.number().min(1).max(5).optional(),
      maxFlaggedRate: z.number().min(0).max(1).optional(),
      maxLeafDeficitFraction: z.number().min(0).max(1).optional(),
    })
    .optional(),
});

export type QcThresholds = z.infer<typeof QcThresholdsSchema>;

export const VerticalConfigSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  shortName: z.string(),
  description: z.string(),
  iconPath: z.string(),

  // Industry grouping for the credential picker + marketing site. Purely
  // presentational (see CATEGORIES) — no engine/app logic branches on it.
  category: z.enum(CATEGORY_SLUGS),

  capabilities: CapabilityFlagsSchema,
  pricing: PricingConfigSchema,

  // Whether this vertical is exposed to customers. The seeder applies this
  // ONLY on first create and never overwrites it on re-seed, so a deliberate
  // prod toggle is preserved. Defaults false: a new vertical ships dark until
  // an intentional go-to-market flip. Live verticals set this true explicitly.
  // Omitted ⇒ treated as false by the seeder.
  isLive: z.boolean().optional(),

  files: z.object({
    taxonomy: z.string(),
    blueprint: z.string(),
    seedQuestions: z.string(),
    scenarios: z.string().optional(),
    voiceExercises: z.string().optional(),
    systemPrompts: z.string(),
    marketing: z.string(),
  }),

  seoPatterns: z.array(SeoPatternSchema),
  sourceRegistry: z.array(SourceRegistryEntrySchema),

  // Optional quality-gate overrides. See QcThresholdsSchema.
  qcThresholds: QcThresholdsSchema.optional(),

  // Pipeline execution mode for content generation.
  //
  // "realtime" (default): synchronous pipeline — chunks flow through
  // classify → plan → generate → judge → rescue → faithfulness in
  // seconds. Use for launch verticals where we're iterating live and
  // need fast feedback. Costs ~30% more per draft than batch (no
  // Anthropic Batch API discount).
  //
  // "batch": staged async pipeline driven by a saga state machine on
  // PipelineRun.stage. Each layer's calls are submitted as one
  // Anthropic Batch (50% cheaper, 24h SLA — typically <1h actual).
  // The step-runner (tools/advance-batch-runs.ts) polls Anthropic for
  // completed batches and advances each PipelineRun through its
  // stages. Use for fast-follower verticals where we're seeding 200+
  // questions and don't need synchronous feedback.
  //
  // CLI flag --mode overrides this per run. Defaults to "realtime"
  // when omitted (handled at read-time in pipeline.ts to keep this
  // field truly optional in the inferred VerticalConfig type — Zod's
  // .default() makes the runtime parse permissive but the static type
  // requires the field, which forces every existing vertical.config
  // to declare it).
  pipelineMode: z.enum(["realtime", "batch"]).optional(),
});

export type VerticalConfig = z.infer<typeof VerticalConfigSchema>;
export type CapabilityFlags = z.infer<typeof CapabilityFlagsSchema>;
export type PricingConfig = z.infer<typeof PricingConfigSchema>;
export type IapProductIds = z.infer<typeof IapProductIdsSchema>;
export type IapOfferIds = z.infer<typeof IapOfferIdsSchema>;
export type SeoPattern = z.infer<typeof SeoPatternSchema>;
export type VoiceExercise = z.infer<typeof VoiceExerciseSchema>;
export type SourceRegistryEntry = z.infer<typeof SourceRegistryEntrySchema>;
